import {
  AsyncPipe,
  NgClass,
  NgFor,
  NgIf,
  NgStyle,
  TitleCasePipe,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  Dossier,
  Fitter,
  getGlassNameKey,
  getOperationWithGlassKey,
} from '@sc/data-access-models';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import {
  IconComponent,
  InsurerLogoComponent,
  MomentParseZonePipe,
  SpinnerComponent,
} from '@sc/ui-components';
import { dayjs, getTranslatableDuration, isToday } from '@sc/utils';
import { PlanningCalendarService } from '../../../../services';
import { MobilePlanningCalendarHeaderComponent } from './../mobile-planning-calendar-header';

@Component({
  selector: 'sc-mobile-planning-calendar',
  styleUrls: ['./mobile-planning-calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InsurerLogoComponent,
    IconComponent,
    NgClass,
    NgIf,
    MatDatepickerModule,
    NgFor,
    NgStyle,
    SpinnerComponent,
    AsyncPipe,
    TitleCasePipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <div class="mobile-calendar-header">
      <div class="header-logo"><sc-insurer-logo></sc-insurer-logo></div>
      <div class="header-nav">
        <sc-icon name="arrow_left" (click)="onGoBack()"></sc-icon>
        <span class="header-fitter">{{ fitter?.brand }}</span>
      </div>
      <div class="header-calendar">
        <div class="navigation">
          <button class="arrow" (click)="onDateNavigation(false)">
            <sc-icon name="arrow_left" tabindex="0"></sc-icon>
          </button>

          <div
            (click)="calendarToggled = !calendarToggled"
            class="header-date"
            [ngClass]="{
              toggled: calendarToggled
            }"
          >
            <div class="day">{{ dayLabel | titlecase }}</div>
          </div>
          <button class="arrow" (click)="onDateNavigation(true)">
            <sc-icon name="arrow_right" tabindex="0"></sc-icon>
          </button>
        </div>
      </div>
    </div>
    <div
      class="calendar-row"
      [ngClass]="{
        toggled: calendarToggled
      }"
      *ngIf="calendarToggled"
    >
      <mat-calendar
        #calendarDatePicker
        [startAt]="date"
        (selectedChange)="dateChanged($event)"
        [headerComponent]="calendarHeader"
      ></mat-calendar>
    </div>

    <ng-container *ngIf="!isLoading && groupedDossiers?.size">
      <div
        class="mobile-calendar-body"
        *ngFor="let dossierDate of getAllAvailableDays()"
      >
        <div class="mobile-calendar-body-date">
          <span class="mobile-calendar-week-date">{{
            dossierDate
              | parseZoneTimeString: timezoneService.getFitterTimezone(fitter)
              | localeDate: 'EEE'
              | async
          }}</span>
          <span
            class="mobile-calendar-short-date"
            [ngClass]="{
              today: isToday(dossierDate)
            }"
            >{{
              dossierDate
                | parseZoneTimeString: timezoneService.getFitterTimezone(fitter)
                | localeDate: 'dd'
                | async
            }}</span
          >
        </div>
        <div class="mobile-calendar-body-slots">
          <ng-container
            *ngFor="let dossier of groupedDossiers?.get(dossierDate)"
          >
            <div class="mobile-calendar-slots-title">
              <p>
                <strong>
                  {{
                    dossier?.appointment?.time
                      | parseZoneTimeString
                        : timezoneService.getFitterTimezone(fitter)
                      | localeDate: 'HH:mm'
                      | async
                  }}
                  -
                  {{
                    dossier?.appointment?.endTime
                      | parseZoneTimeString
                        : timezoneService.getFitterTimezone(fitter)
                      | localeDate: 'HH:mm'
                      | async
                  }}</strong
                >
                ({{ getDuration(dossier?.appointment?.interventionDuration) }} -
                {{ getRequiredOperationText(dossier) }})
              </p>
            </div>
            <div
              class="mobile-calendar-slots-container"
              (click)="goToDossierForm(dossier.id)"
            >
              <div
                class="tooltip-agenda dossier-event"
                [ngStyle]="{
                  'border-color': planningCalendarService.getDossierEventColor(
                    dossier,
                    fitter
                  ),
                  background: planningCalendarService.getDossierEventBackground(
                    dossier,
                    fitter
                  )
                }"
              >
                <div class="dossier-event-infos">
                  <p class="font-family-bold font-s">
                    <strong>
                      {{
                        dossier?.claim?.car?.manufacturer &&
                        dossier?.claim?.car?.model
                          ? dossier?.claim?.car?.manufacturer +
                            ' ' +
                            dossier?.claim?.car?.model
                          : this.translator.getInstantTranslation(
                              'PlanningEventTooltip.vehicleNotFound'
                            )
                      }}</strong
                    >
                  </p>

                  <p class="font-family-main font-xs">
                    {{
                      dossier?.claim?.car?.licensePlateNumber
                        ? dossier?.claim?.car?.licensePlateNumber
                        : ''
                    }}
                  </p>

                  <p class="font-family-main font-xs">
                    {{
                      dossier?.claim?.car?.vehicleType
                        ? this.translator.getInstantTranslation(
                            'PlanningFormAppointmentIntervention.vehicleTypeNames.' +
                              dossier?.claim?.car?.vehicleType
                          )
                        : ''
                    }}
                  </p>
                  <p class="font-family-main font-xs uppercase">
                    {{
                      dossier?.customer?.lastname +
                        ' ' +
                        dossier?.customer?.firstname
                    }}
                  </p>

                  <p
                    *ngIf="dossier?.claim?.calibration === 'true'"
                    class="font-family-main font-xs"
                  >
                    {{ 'MobilePlanningCalendar.calibrationLabel' | translate }}
                  </p>
                </div>

                <div class="dossier-event-left">
                  <span [innerHTML]="getIcons(dossier)"></span>
                  <span *ngIf="dossier?.appointment?.assignedResources?.length">
                    <p
                      *ngFor="
                        let technician of dossier?.appointment
                          ?.assignedResources
                      "
                      class="font-family-main font-xs uppercase"
                    >
                      <strong> {{ technician }} </strong>
                    </p>
                  </span>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </ng-container>
    <div class="loader" *ngIf="isLoading">
      <sc-spinner></sc-spinner>
    </div>
  `,
})
export class MobilePlanningCalendarComponent {
  calendarHeader = MobilePlanningCalendarHeaderComponent;
  calendarToggled = false;
  @Input() fitter: Fitter;
  @Input() set currentDate(date: Date) {
    this.date = date;
    this.initDayText();
    this.changeDetectorRef.detectChanges();
  }
  @Input() groupedDossiers: Map<string, Dossier[]>;
  @Input() isLoading: boolean;
  @Output() backClick = new EventEmitter<any>();
  @Output() dossierClick = new EventEmitter<string>();
  @Output() dateChange = new EventEmitter<Date | string>();

  date: Date;

  @ViewChild(MatCalendar) calendarDatePicker: MatCalendar<Date>;
  dayLabel: string;

  constructor(
    public translator: TranslatorService,
    private readonly _dateAdapter: DateAdapter<Date>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public planningCalendarService: PlanningCalendarService,
    public timezoneService: TimezoneService,
    private readonly configurationService: ConfigurationService
  ) {}

  getDuration(duration: number) {
    const durationObj = getTranslatableDuration(duration);
    return this.translator.getInstantTranslation(
      durationObj?.value,
      durationObj.param
    );
  }

  getRequiredOperationText(dossier: Dossier): string {
    return this.translator.getInstantTranslation(
      getOperationWithGlassKey(dossier?.claim?.operationType),
      {
        glassName: this.translator
          .getInstantTranslation(getGlassNameKey(dossier.claim.glass))
          .toLowerCase(),
      }
    );
  }

  isToday(date: Date | string) {
    return isToday(date);
  }

  getAllAvailableDays() {
    return Array.from(this.groupedDossiers?.keys() || []);
  }

  getIcons(dossier: Dossier) {
    return this.planningCalendarService.generateIconsForTooltipEvent(
      dossier,
      this.configurationService
    );
  }

  initDayText() {
    this.dayLabel = dayjs(this.date)
      .tz(this.timezoneService.getFitterTimezone(this.fitter))
      .format('MMMM YYYY')
      ?.toString()
      ?.toLowerCase();
  }

  dateChanged(dayjs) {
    this.initDayText();
    this.dateChange.emit(dayjs);
  }

  onGoBack() {
    this.backClick.emit();
  }

  onDateNavigation(isNext: boolean) {
    const date = dayjs(this.date)
      .add(isNext ? 1 : -1, 'M')
      ?.toDate();
    if (this.calendarDatePicker) {
      this.calendarDatePicker.activeDate = this._dateAdapter.addCalendarMonths(
        this.calendarDatePicker.activeDate,
        isNext ? 1 : -1
      );
    }
    this.dateChange.emit(new Date(date));
  }

  goToDossierForm(dossierId: string) {
    this.dossierClick.emit(dossierId);
    //
  }
}
